import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Image,
  ViewStyle,
  ImageStyle,
  TextStyle,
  useWindowDimensions,
} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {getAuthData, orgRole} from 'redux/selectors/authSelector';
import {Icon, Text} from 'ui-kits';
import {MENU_CONFIG, navProps} from 'constants/MenuOptions';
import LinearGradient from 'react-native-linear-gradient';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams} from 'navigation/Routes';
import {corner_radius, spacing} from 'theme/Units';
import {defaultUserProfile, mobileViewportWidth} from 'constants/DefaultValues';
import colors from 'theme/Colors';
import {useRoute} from '@react-navigation/native';
import {isParentOf} from 'utils/navigation';
import {resetAuth} from 'redux/slices/authSlice';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';

const logoutProps = {
  label: 'Logout',
  icon: 'logout_gray',
  iconActive: 'logout_white',
};

export function SideNavbar({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const user_role = useSelector(orgRole);
  const user_data = useSelector(getAuthData);
  const dispatch = useDispatch();
  const {width} = useWindowDimensions();
  const isWeb = width > mobileViewportWidth;

  const menuItems = user_role ? MENU_CONFIG[user_role] : [];

  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);

  return (
    <View style={styles.sideNavbar}>
      <View style={styles.profile}>
        <Image
          source={{uri: user_data?.profile_photo_url ?? defaultUserProfile}}
          style={styles.profilePhoto}
        />
        <View style={styles.basicDetails}>
          <Text variant={isWeb ? 'sourcesans18m' : 'sourcesans16m'}>
            {user_data?.first_name.concat(' ', user_data.last_name)}
          </Text>
          <Text variant={isWeb ? 'sourcesans14' : 'sourcesans12'}>
            {user_data?.email}
          </Text>
        </View>
      </View>
      <View style={styles.menuContainer}>
        {menuItems.map(item => (
          <DropDownNavlink
            key={item.route}
            {...item}
            onPress={() => navigation.replace(item.route)}
          />
        ))}
        <DropDownNavlink
          {...logoutProps}
          onPress={() => dispatch(resetAuth())}
        />
      </View>
    </View>
  );
}
const gradientColors = [
  `${colors.palette.brandBlue}`,
  `${colors.palette.brandPurple}`,
];

function DropDownNavlink(props: Readonly<navProps>) {
  const {icon, iconActive, label, route: routeString, onPress} = props;
  const route = useRoute();
  const isActive = isParentOf(route.name, routeString);
  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);
  const {width} = useWindowDimensions();
  const isWeb = width > mobileViewportWidth;
  return (
    <TouchableOpacity onPress={onPress}>
      <LinearGradient
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}
        colors={isActive ? gradientColors : ['white', 'white']}
        //onLayout={onGradientLayout}
        style={styles.gradient}>
        <View style={styles.menuItem}>
          <Icon size={isWeb ? 28 : 24} type={isActive ? iconActive : icon} />
          <Text
            variant="sourcesans16"
            style={isActive ? styles.activeMenuLabel : styles.menuLabel}>
            {label}
          </Text>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );
}
type styleTypes = {
  sideNavbar: ViewStyle;
  profile: ViewStyle;
  profilePhoto: ImageStyle;
  gradient: ViewStyle;
  menuItem: ViewStyle;
  menuContainer: ViewStyle;
  menuLabel: TextStyle;
  activeMenuLabel: TextStyle;
};
const baseStyles = StyleSheet.create({
  basicDetails: {
    gap: spacing['1'],
  },
  sideNavbar: {
    backgroundColor: 'white',
    paddingHorizontal: spacing['5'],
    paddingVertical: spacing['3'],
    width: '100%',
    borderRadius: corner_radius['rounded-md'],
    shadowColor: 'rgba(0, 0, 0, 0.15)',
    shadowOffset:{height:2,width:-1},
    shadowRadius: 4,
  },
  profile: {
    flexDirection: 'row',
    gap: spacing['3'],
    alignItems: 'center',
    marginBottom: spacing['4'],
  },
  profilePhoto: {
    width: 60,
    height: 60,
    borderRadius: 999,
  },
  gradient: {
    borderRadius: corner_radius['rounded-md'],
    paddingLeft: spacing['3'],
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing['1'],
    gap: spacing['3'],
  },
  menuLabel: {
    color: colors.palette.secondaryBlack5,
  },
  activeMenuLabel: {
    color: colors.palette.white,
  },
  menuContainer: {
    gap: spacing['2'],
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  480: StyleSheet.create({
    profilePhoto: {
      width: 40,
      height: 40,
      borderRadius: 999,
    },
    menuLabel: {
      fontSize: 14,
    },
    activeMenuLabel: {
      fontSize: 14,
    },
  }),
};
